type AnalyticsEventParams = Record<string, unknown>;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID?.trim();
const GA_LOCAL_MEASUREMENT_ID = process.env.REACT_APP_GA_LOCAL_MEASUREMENT_ID?.trim();
const GA_STAGING_MEASUREMENT_ID = process.env.REACT_APP_GA_STAGING_MEASUREMENT_ID?.trim();
const GA_ENVIRONMENT_OVERRIDE = process.env.REACT_APP_GA_ENVIRONMENT?.trim().toLowerCase();
const GA_DEBUG_MODE = (process.env.REACT_APP_GA_DEBUG_MODE || '').trim().toLowerCase() === 'true';
const GA_PRODUCTION_HOSTNAMES = (process.env.REACT_APP_GA_PROD_HOSTNAMES || 't-accounts.com,www.t-accounts.com')
  .split(',')
  .map((hostname) => hostname.trim().toLowerCase())
  .filter(Boolean);
let hasLoggedStartupSelection = false;

function isLocalHost(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1';
}

function getMeasurementId(): string | null {
  if (isLocalHost()) {
    return GA_LOCAL_MEASUREMENT_ID || null;
  }

  if (isStagingHost()) {
    return GA_STAGING_MEASUREMENT_ID || GA_MEASUREMENT_ID || null;
  }

  return GA_MEASUREMENT_ID || null;
}

function isStagingHost(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  if (GA_ENVIRONMENT_OVERRIDE === 'staging') {
    return true;
  }

  if (GA_ENVIRONMENT_OVERRIDE === 'prod' || GA_ENVIRONMENT_OVERRIDE === 'production') {
    return false;
  }

  const hostname = window.location.hostname.toLowerCase();
  const isAzureStaticAppsHost = hostname.endsWith('.azurestaticapps.net');
  const isKnownProductionHost = GA_PRODUCTION_HOSTNAMES.includes(hostname);

  return isAzureStaticAppsHost && !isKnownProductionHost;
}

function getAppEnvironment(): string {
  if (GA_ENVIRONMENT_OVERRIDE) {
    return GA_ENVIRONMENT_OVERRIDE;
  }

  if (isLocalHost()) {
    return 'local';
  }

  if (isStagingHost()) {
    return 'staging';
  }

  return 'prod';
}

function buildDefaultEventParams(): AnalyticsEventParams {
  const params: AnalyticsEventParams = {
    app_environment: getAppEnvironment(),
  };

  if (GA_DEBUG_MODE && isLocalHost()) {
    params.debug_mode = true;
  }

  return params;
}

function buildLocalTransportParams(): AnalyticsEventParams {
  if (!isLocalHost()) {
    return {};
  }

  return {
    transport_type: 'beacon',
  };
}

function isAnalyticsEnabled(): boolean {
  return Boolean(getMeasurementId()) && typeof window !== 'undefined' && typeof document !== 'undefined';
}

export function initializeAnalytics(): void {
  const measurementId = getMeasurementId();
  const appEnvironment = getAppEnvironment();

  if (!hasLoggedStartupSelection && (appEnvironment === 'staging' || appEnvironment === 'prod') && typeof window !== 'undefined') {
    console.info('[analytics] Startup selection', {
      app_environment: appEnvironment,
      hostname: window.location.hostname,
      measurement_id: measurementId,
    });
    hasLoggedStartupSelection = true;
  }

  if (!isAnalyticsEnabled() || !measurementId) {
    return;
  }

  if (document.getElementById('ga4-gtag-script')) {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function gtag(this: unknown) {
      window.dataLayer?.push(arguments);
    };
  }

  const script = document.createElement('script');
  script.id = 'ga4-gtag-script';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  window.gtag?.('js', new Date());
  window.gtag?.('config', measurementId, {
    send_page_view: false,
    anonymize_ip: true,
    ...buildDefaultEventParams(),
  });

  document.head.appendChild(script);
}

export function trackPageView(path: string, pageName: string): void {
  if (!isAnalyticsEnabled()) {
    return;
  }

  initializeAnalytics();
  const payload: AnalyticsEventParams = {
    page_path: path,
    page_location: typeof window !== 'undefined' ? window.location.href : path,
    page_title: document.title,
    page_name: pageName,
    ...buildDefaultEventParams(),
    ...buildLocalTransportParams(),
  };
  window.gtag?.('event', 'page_view', payload);
}

export function trackEvent(eventName: string, params: AnalyticsEventParams = {}): void {
  if (!isAnalyticsEnabled()) {
    return;
  }

  initializeAnalytics();
  const payload: AnalyticsEventParams = {
    ...buildDefaultEventParams(),
    ...buildLocalTransportParams(),
    ...params,
  };
  window.gtag?.('event', eventName, payload);
}
